import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, useTheme,
  CircularProgress, IconButton, Tooltip, useMediaQuery, TextField
} from '@mui/material';
import { ArrowBack, Download, Refresh, Search } from '@mui/icons-material';
import { useGetEmpDataAdminQuery } from 'state/api';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';

const RmDetails = () => {
  const { RmMgName } = useSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState("");
  
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: response, isLoading, isError, refetch } = useGetEmpDataAdminQuery({
    RmMgName: RmMgName || ''
  });

  // Extract actual data array from the response
  const data = response?.data || [];
  const success = response?.success || false;
  
  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(item => 
      (item.MrName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.DoctName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Extract dynamic date columns
  const { dateGroups, products } = useMemo(() => {
    const groups = {};
    const productSet = new Set();
    
    if (data.length > 0) {
      for (const key in data[0]) {
        if (key.match(/^\d{2}_\d{2}_\d{4}_[a-zA-Z]+_(Approved|Pending|Rejected)$/)) {
          const [day, month, year, product, status] = key.split('_');
          const dateStr = `${day}_${month}_${year}`;
          
          if (!groups[dateStr]) groups[dateStr] = {};
          if (!groups[dateStr][product]) groups[dateStr][product] = [];
          
          groups[dateStr][product].push(status);
          productSet.add(product);
        }
      }
    }
    
    // Sort dates chronologically
    const sortedDates = Object.keys(groups).sort((a, b) => {
      const [aDay, aMonth, aYear] = a.split('_');
      const [bDay, bMonth, bYear] = b.split('_');
      const dateA = new Date(`${aYear}-${aMonth}-${aDay}`);
      const dateB = new Date(`${bYear}-${bMonth}-${bDay}`);
      return dateA - dateB;
    });
    
    const sortedGroups = {};
    sortedDates.forEach(date => {
      sortedGroups[date] = groups[date];
    });
    
    return {
      dateGroups: sortedGroups,
      products: Array.from(productSet)
    };
  }, [data]);

  // Format date to DD MMM
  const formatDateHeader = (dateStr) => {
    const [day, month, year] = dateStr.split('_');
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  };

  const handleExport = () => {
    // Create a new array with formatted headers
    const exportData = data.map(row => {
      const newRow = {...row};
      for (const key in newRow) {
        if (key.match(/^\d{2}_\d{2}_\d{4}_[a-zA-Z]+_(Approved|Pending|Rejected)$/)) {
          const [day, month, year, , status] = key.split('_');
          const formattedKey = `${day}/${month}/${year} ${status}`;
          newRow[formattedKey] = newRow[key];
          delete newRow[key];
        }
      }
      return newRow;
    });
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Doctor Approvals");
    XLSX.writeFile(workbook, `${RmMgName || 'Export'}_approvals.xlsx`);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  if (isLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: theme.palette.background.default
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isError || !success) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 3,
        textAlign: 'center',
        padding: 3,
        background: theme.palette.background.default
      }}>
        <Typography variant="h5" color="error">
          {isError ? "Error loading data" : "No data available"}
        </Typography>
        <Button 
          variant="outlined"
          startIcon={<Refresh />}
          onClick={refetch}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      padding: { xs: 2, md: 4 },
      maxWidth: '1800px',
      margin: '0 auto',
      minHeight: '100vh',
      background: theme.palette.background.default
    }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 3,
        mb: 4,
        padding: 3,
        borderRadius: 4,
        background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: 3
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          width: { xs: '100%', md: 'auto' }
        }}>
          <IconButton onClick={() => navigate(-1)} sx={{ 
            color: theme.palette.common.white,
            background: 'rgba(255,255,255,0.2)',
            '&:hover': {
              background: 'rgba(255,255,255,0.3)'
            }
          }}>
            <ArrowBack />
          </IconButton>

          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: theme.palette.common.white,
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}>
            {RmMgName || 'Medical Representative'}
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex',
          gap: 2,
          width: { xs: '100%', md: 'auto' },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <TextField
            label="Search Employees/Doctors"
            variant="filled"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <Search sx={{ color: theme.palette.action.active, mr: 1 }} />,
              disableUnderline: true,
            }}
            sx={{
              flexGrow: 1,
              '& .MuiFilledInput-root': {
                background: theme.palette.background.paper,
                borderRadius: 2,
                '&:hover': { background: theme.palette.background.paper }
              }
            }}
          />
          
          <Button
            onClick={handleExport}
            variant="contained"
            startIcon={<Download />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              background: theme.palette.secondary.main,
              color: theme.palette.getContrastText(theme.palette.secondary.main),
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 4,
                background: theme.palette.secondary.dark,
              },
              transition: 'all 0.3s ease',
              minWidth: '140px'
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      {filteredData.length > 0 ? (
        <Paper sx={{
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: theme.shadows[3],
          background: theme.palette.background.paper,
          overflowX: 'auto'
        }}>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                {/* Date Header Row */}
                <TableRow>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.common.white,
                      position: 'sticky',
                      left: 0,
                      zIndex: 20,
                      minWidth: 180,
                      verticalAlign: 'middle'
                    }}
                  >
                    Employee
                  </TableCell>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.common.white,
                      position: 'sticky',
                      left: isMobile ? 90 : 180,
                      zIndex: 20,
                      minWidth: 180,
                      verticalAlign: 'middle'
                    }}
                  >
                    Doctor
                  </TableCell>
                  {Object.keys(dateGroups).map(date => (
                    <TableCell 
                      key={`date-${date}`}
                      align="center"
                      colSpan={Object.keys(dateGroups[date]).length * 3}
                      sx={{ 
                        fontWeight: 'bold', 
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white
                      }}
                    >
                      {formatDateHeader(date)}
                    </TableCell>
                  ))}
                </TableRow>
                
                {/* Product & Status Header Row */}
                <TableRow>
                  {Object.keys(dateGroups).flatMap(date => 
                    Object.keys(dateGroups[date]).flatMap(product => 
                      ['Approved', 'Rejected', 'Pending'].map(status => (
                        <TableCell 
                          key={`${date}-${product}-${status}`}
                          align="center"
                          sx={{ 
                            fontWeight: 'bold',
                            backgroundColor: 
                              status === 'Approved' ? theme.palette.success.light :
                              status === 'Rejected' ? theme.palette.error.light :
                              theme.palette.warning.light,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {product} - {status}
                        </TableCell>
                      ))
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      '&:nth-of-type(odd)': {
                        background: theme.palette.action.hover
                      },
                      '&:hover': {
                        background: theme.palette.action.selected
                      }
                    }}
                  >
                    <TableCell
                      sx={{ 
                        position: 'sticky',
                        left: 0,
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 10,
                        minWidth: 180
                      }}
                    >
                      <Typography fontWeight={600}>
                        {row.MrName || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{ 
                        position: 'sticky',
                        left: isMobile ? 90 : 180,
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 10,
                        minWidth: 180
                      }}
                    >
                      <Typography color="textSecondary">
                        {!row.DoctName || row.DoctName === '\\N' ? 'N/A' : row.DoctName}
                      </Typography>
                    </TableCell>
                    
                    {Object.keys(dateGroups).flatMap(date => 
                      Object.keys(dateGroups[date]).flatMap(product => 
                        ['Approved', 'Rejected', 'Pending'].map(status => {
                          const key = `${date}_${product}_${status}`;
                          return (
                            <TableCell 
                              key={key}
                              align="center"
                              sx={{ 
                                backgroundColor: 
                                  status === 'Approved' ? theme.palette.success.light :
                                  status === 'Rejected' ? theme.palette.error.light :
                                  theme.palette.warning.light
                              }}
                            >
                              <Typography sx={{ 
                                fontWeight: 'bold',
                                color: getStatusColor(status, theme)
                              }}>
                                {row[key] || '0'}
                              </Typography>
                            </TableCell>
                          );
                        })
                      )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          textAlign: 'center',
          mt: 4,
          flexDirection: 'column',
          gap: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 4,
          background: theme.palette.background.paper
        }}>
          <Typography variant="h6" color="textSecondary">
            {searchTerm ? "No matching records found" : "No approval records found"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Helper function to get status color
const getStatusColor = (status, theme) => {
  switch(status) {
    case 'Approved':
      return theme.palette.success.dark;
    case 'Pending':
      return theme.palette.warning.dark;
    case 'Rejected':
      return theme.palette.error.dark;
    default:
      return theme.palette.text.primary;
  }
};

export default RmDetails;